import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import { Badge } from 'reactstrap';
import paymentBadgeFormatter from '../formatters/badgeFormatter';
import { APPLICATION_STATUS } from 'Types/constants';
import { RENEWAL_STATUS } from 'Types/constants';

/**
 * Formatter for user roles - displays roles as colored badges with icons
 * @param {Array} role - Array of role strings
 * @returns {JSX.Element} - Formatted role badges
 */
export const roleFormatter = role => {
  if (role && role.length > 0) {
    return (
      <div className="d-flex flex-wrap">
        {role.map((singleRole, index) => (
          <span key={index} className="badge badge-info rounded-pill mr-1 mb-1 d-inline-flex align-items-center">
            <FontAwesomeIcon icon="user" className="mr-1" />
            {singleRole}
          </span>
        ))}
      </div>
    );
  }
  return (
    <span className="badge badge-info rounded-pill d-inline-flex align-items-center">
      <FontAwesomeIcon icon="user" className="mr-1" />
      User
    </span>
  );
};

/**
 * Formatter for user levels - displays levels as colored badges with icons
 * @param {string} level - User level string
 * @returns {JSX.Element} - Formatted level badge
 */
export const levelFormatter = level => {
  const getLevelConfig = level => {
    switch (level) {
      case 'ADMIN':
        return {
          color: 'badge-success',
          icon: 'user-shield',
          text: 'Admin'
        };
      case APPLICATION_STATUS.INCOMPLETE_MEMBER:
        return {
          color: 'badge-warning',
          icon: 'minus',
          text: 'Incomplete Member'
        };
      case APPLICATION_STATUS.PENDING_VERIFICATION_MEMBER:
        return {
          color: 'badge-secondary',
          icon: 'user-edit',
          text: 'Pending Verification'
        };
      case APPLICATION_STATUS.PENDING_APPROVAL_MEMBER:
        return {
          color: 'badge-info',
          icon: 'user-plus',
          text: 'Pending Approval'
        };
      case APPLICATION_STATUS.APPROVED_MEMBER:
        return {
          color: 'badge-primary',
          icon: 'check',
          text: 'Approved Member'
        };
      case APPLICATION_STATUS.DELETED_MEMBER:
        return {
          color: 'badge-danger',
          icon: 'user-times',
          text: 'Deleted'
        };
      default:
        return {
          color: 'badge-secondary',
          icon: 'user',
          text: level || 'Unknown'
        };
    }
  };

  const config = getLevelConfig(level);

  return (
    <span className={`badge ${config.color} rounded-pill d-inline-flex align-items-center`}>
      <FontAwesomeIcon icon={config.icon} className="mr-1" />
      {config.text}
    </span>
  );
};

export const renewalStatusFormatter = renewalStatus => {
  const getLevelConfig = renewalStatus => {
    switch (renewalStatus) {
      case RENEWAL_STATUS.NOT_STARTED:
        return {
          color: 'badge-danger',
          icon: 'ban',
          text: 'Renewal - Not Started'
        };
      case RENEWAL_STATUS.RENEWAL_PENDING_VERIFICATION:
        return {
          color: 'badge-secondary',
          icon: 'user-edit',
          text: 'Renewal - Pending Verification'
        };
      case RENEWAL_STATUS.RENEWAL_PENDING_APPROVAL:
        return {
          color: 'badge-info',
          icon: 'user-plus',
          text: 'Renewal - Pending Approval'
        };
      case RENEWAL_STATUS.RENEWED:
        return {
          color: 'badge-primary',
          icon: 'check',
          text: 'Renewed - Member'
        };
      case RENEWAL_STATUS.PROCESSING:
        return {
          color: 'badge-warning',
          icon: 'user-times',
          text: 'Renewal - Processing'
        };
      default:
        return {
          color: 'badge-secondary',
          icon: 'user',
          text: 'Renewal Status Unknown'
        };
    }
  };

  const config = getLevelConfig(renewalStatus);

  return (
    <span className={`badge ${config.color} rounded-pill d-inline-flex align-items-center`}>
      <FontAwesomeIcon icon={config.icon} className="mr-1" />
      {config.text}
    </span>
  );
};

/**
 * Formatter to display UID and email on two separate lines
 * @param {string} uid
 * @param {string} email
 * @returns {JSX.Element}
 */
export const uidEmailFormatter = (uid, email) => {
  return (
    <div>
      <div>{email ? email : <span className="text-muted">—</span>}</div>
      <div style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{uid || '—'}</div>
    </div>
  );
};

// Currency formatter supporting USD and ZWL with graceful fallback
export const formatCurrencyAmount = (amount, currency) => {
  const value = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(value)) return '—';

  const code = String(currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-ZW', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch (e) {
    return `${code}${value.toFixed(2)}`;
  }
};

// Common date format enum (frozen to prevent mutation)
export const DateFormats = Object.freeze({
  DD_MMM_YYYY: 'dd-MMM-yyyy',
  DD_MM_YYYY: 'dd/MM/yyyy',
  YYYY_MM_DD: 'yyyy-MM-dd',
  DD_MMM_YYYY_HH_MM: 'dd-MMM-yyyy HH:mm',
  DD_MM_YYYY_HH_MM: 'dd/MM/yyyy HH:mm',
  ISO_DATE_TIME: 'yyyy-MM-dd HH:mm:ss'
});

// Helper: pad number with leading zeros
const pad = (num, size = 2) => String(num).padStart(size, '0');

// Helper: month names
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

/**
 * Format a UTC ISO date string into local time using a given pattern
 * Supported tokens: yyyy, yy, MMMM, MMM, MM, M, dd, d, HH, H, mm, m, ss, s
 * @param {string|Date|number} isoUtc - ISO string like 2025-09-19T21:43:20.230Z, a Date, or epoch ms
 * @param {string} format - Pattern from DateFormats (e.g., dd-MMM-yyyy)
 * @returns {string} formatted date or '—' when invalid
 */
export const formatLocalDate = (isoUtc, format = DateFormats.DD_MMM_YYYY) => {
  if (!isoUtc) return '—';
  const d = isoUtc instanceof Date ? isoUtc : new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return '—';

  const year = d.getFullYear();
  const monthIndex = d.getMonth(); // 0-based
  const date = d.getDate();
  const hours = d.getHours();
  const minutes = d.getMinutes();
  const seconds = d.getSeconds();

  const replacements = {
    yyyy: String(year),
    yy: String(year).slice(-2),
    MMMM: MONTHS_LONG[monthIndex],
    MMM: MONTHS_SHORT[monthIndex],
    MM: pad(monthIndex + 1),
    M: String(monthIndex + 1),
    dd: pad(date),
    d: String(date),
    HH: pad(hours),
    H: String(hours),
    mm: pad(minutes),
    m: String(minutes),
    ss: pad(seconds),
    s: String(seconds)
  };

  // Replace tokens longest-first to avoid partial overlaps
  const order = ['yyyy', 'yy', 'MMMM', 'MMM', 'MM', 'M', 'dd', 'd', 'HH', 'H', 'mm', 'm', 'ss', 's'];
  let out = format;
  order.forEach(tok => {
    out = out.replace(new RegExp(tok, 'g'), replacements[tok]);
  });
  return out;
};

// Service request status badge (verification/approval lifecycle)
export const requestStatusBadge = status => {
  const s = String(status || '').toUpperCase();
  const map = {
    PENDING_VERIFICATION: { color: 'danger', icon: 'stream', text: 'Pending Verification' },
    PENDING_APPROVAL: { color: 'info', icon: 'stream', text: 'Pending Approval' },
    NOT_APPROVED: { color: 'warning', icon: 'stream', text: 'Not Approved' },
    APPROVED: { color: 'success', icon: 'check', text: 'Approved' }
  };
  const cfg = map[s] || { color: 'warning', icon: 'stream', text: 'Pending - Blocked' };
  return (
    <Badge color={`soft-${cfg.color}`} className="rounded-capsule">
      {cfg.text}
      <FontAwesomeIcon icon={cfg.icon} transform="shrink-2" className="ml-1" />
    </Badge>
  );
};

// Combined Payment cell: amount (top) + payment status badge (bottom)
export const paymentCellFormatter = (cell, row) => {
  const amount =
    row?.fee !== undefined && row?.fee !== null
      ? formatCurrencyAmount(row.fee, row?.currency || row?.feeCurrency || 'USD')
      : '—';
  return (
    <div className="d-flex flex-column align-items-end">
      <div className="font-weight-semi-bold">{amount}</div>
      <div className="mt-1">{paymentBadgeFormatter(row?.paymentStatus)}</div>
    </div>
  );
};

// Stacked: name (link) on top, transaction id below
export const nameWithTransactionFormatter = (name, row) => (
  <div className="d-flex flex-column">
    <Link to={'#'} className="font-weight-semi-bold">
      {name}
    </Link>
    {row?.transactionId ? <div className="small text-muted mt-1">Txn: {row.transactionId}</div> : null}
  </div>
);

// Combined status + date cell: status badge (top), date (bottom)
export const statusDateFormatter = (cell, row) => (
  <div className="d-flex flex-column">
    <div>{requestStatusBadge(row?.status)}</div>
    <div className="small text-muted mt-1">
      {row?.applicationDate ? String(row.applicationDate).substring(0, 10) : ''}
    </div>
  </div>
);

// Factory for a link-style edit badge formatter
export const makeEditBadgeFormatter = ({ toBuilder, isDisabled, title = 'Edit Application' }) => {
  return (cell, row) => {
    const disabled = typeof isDisabled === 'function' ? !!isDisabled(row) : !!isDisabled;
    const to = typeof toBuilder === 'function' ? toBuilder(row) : '#';
    if (disabled) {
      return (
        <Badge
          color="light"
          className="rounded-capsule ml-1 px-2 py-1 d-inline-flex align-items-center text-muted"
          title="Edit disabled"
        >
          <FontAwesomeIcon icon="edit" className="mr-1" />
          Edit Application
        </Badge>
      );
    }
    return (
      <Badge
        tag={Link}
        to={to}
        color="soft-success"
        className="rounded-capsule ml-1 px-2 py-1 d-inline-flex align-items-center"
        onClick={e => e.stopPropagation()}
        title={title}
      >
        <FontAwesomeIcon icon="edit" className="mr-1" />
        Edit Application
      </Badge>
    );
  };
};
